import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from 'src/redux/config';

// ========================================
// THUNKS ASYNC
// ========================================

/**
 * Récupérer les données Claims avec filtres
 */
export const fetchClaimsDashboard = createAsyncThunk(
  'dashboard/fetchClaims',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(
        `/dashboard/claims?${params.toString()}`
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
/**
 * Récupérer le nombre de reviews par nationalité et par secteur
 */
export const fetchReviewsByNationalityAndSector = createAsyncThunk(
  'dashboard/fetchReviewsByNationalityAndSector',
  async ({ country, startDate, endDate }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      if (country) params.append('country', country);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(
        `/dashboard/reviews/by-nationality-sector?${params.toString()}`
      );

      return response.data; // [{ sector, count }]
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
/**
 * Récupérer l'évolution des utilisateurs selon la période
 */
export const fetchUsersEvolution = createAsyncThunk(
  'dashboard/fetchUsersEvolution',
  async (period = 'last_year', { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/dashboard/users/evolution`,
        { params: { period } }
      );
      
      return { data: response.data, period };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


/**
 * Récupérer toutes les données du dashboard
 */
export const fetchAllDashboardData = createAsyncThunk(
  'dashboard/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const [claims, accounts, subscriptions, finance, churn, activity] = await Promise.all([
        api.get(`/dashboard/claims`),
        api.get(`/dashboard/accounts`),
        api.get(`/dashboard/subscriptions`),
        api.get(`/dashboard/finance`),
        api.get(`/dashboard/churn`),
        api.get(`/dashboard/activity`)
      ]);


      return {
        claims: claims.data,
        accounts: accounts.data,
        subscriptions: subscriptions.data,
        finance: finance.data,
        churn: churn.data,
        activity: activity.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ========================================
// SLICE
// ========================================

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    reviewsByNationalitySector: [],
  usersEvolution: [],
  usersEvolutionPeriod: 'last_year',
  usersEvolutionLoading: false,
  reviewsByNationalitySectorLoading: false,
    // Données
    claims: null,
    accounts: null,
    subscriptions: null,
    finance: null,
    churn: null,
    activity: null,
    
    // Legacy support (derived or null)
    companies: null,
    services: null,
    suggestions: null,
    users: null,

    // Filtres Claims
    claimsFilters: {
      sector: '',
      startDate: '2021-01-01',
      endDate: '2022-12-30',
    },

    // États
    loading: false,
    claimsLoading: false,
    errors: [],
  },
  reducers: {
    setUsersEvolutionPeriod: (state, action) => {
    state.usersEvolutionPeriod = action.payload;
  },
    // Mettre à jour les filtres claims
    setClaimsFilters: (state, action) => {
      state.claimsFilters = {
        ...state.claimsFilters,
        ...action.payload,
      };
    },

    // Réinitialiser les filtres
    resetClaimsFilters: (state) => {
      state.claimsFilters = {
        sector: '',
        startDate: '2021-01-01',
        endDate: '2022-12-30',
      };
    },

    // Effacer les erreurs
    clearErrors: (state) => {
      state.errors = [];
    },
  },
  extraReducers: (builder) => {
    builder
  .addCase(fetchUsersEvolution.pending, (state) => {
    state.usersEvolutionLoading = true;
  })
  .addCase(fetchUsersEvolution.fulfilled, (state, action) => {
    state.usersEvolutionLoading = false;
    state.usersEvolution = action.payload.data;
    state.usersEvolutionPeriod = action.payload.period;
  })
  .addCase(fetchUsersEvolution.rejected, (state, action) => {
    state.usersEvolutionLoading = false;
    state.errors.push(
      action.payload || 'Erreur lors du chargement de l\'évolution des utilisateurs'
    );
  });

    // ========================================
    // fetchClaimsDashboard
    // ========================================
    builder
      .addCase(fetchClaimsDashboard.pending, (state) => {
        state.claimsLoading = true;
        state.errors = [];
      })
      .addCase(fetchClaimsDashboard.fulfilled, (state, action) => {
        state.claimsLoading = false;
        state.claims = action.payload;
      })
      .addCase(fetchClaimsDashboard.rejected, (state, action) => {
        state.claimsLoading = false;
        state.errors.push(action.payload || 'Erreur lors du chargement des claims');
      });

    // ========================================
    // fetchAllDashboardData
    // ========================================
    builder
      .addCase(fetchAllDashboardData.pending, (state) => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(fetchAllDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.claims = action.payload.claims;
        state.accounts = action.payload.accounts;
        state.subscriptions = action.payload.subscriptions;
        state.finance = action.payload.finance;
        state.churn = action.payload.churn;
        state.activity = action.payload.activity;
        state.suggestions = action.payload.suggestions;
        
        // Map to legacy structure where possible for compatibility
        state.users = {
           kpis: {
             total: action.payload.accounts?.evolution?.reduce((acc, curr) => acc + curr.count, 0) || 0,
           },
           byAccountType: action.payload.accounts?.accountTypeDistribution?.map(item => ({
             name: item._id,
             value: item.count
           })) || []
        };
        
        state.companies = {
          byBusinessArea: action.payload.accounts?.byBusinessArea?.map(item => ({
            name: item._id,
            count: item.count
          })) || []
        };

        state.services = {
           byBusinessArea: action.payload.claims?.services?.map(item => ({
             name: item._id,
             count: item.count
           })) || []
        };
      })
      .addCase(fetchAllDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload || 'Erreur lors du chargement du dashboard');
      });

      // ========================================
// fetchReviewsByNationalityAndSector
// ========================================
builder
  .addCase(fetchReviewsByNationalityAndSector.pending, (state) => {
    state.reviewsByNationalitySectorLoading = true;
  })
  .addCase(fetchReviewsByNationalityAndSector.fulfilled, (state, action) => {
    state.reviewsByNationalitySectorLoading = false;
    state.reviewsByNationalitySector = action.payload;
  })
  .addCase(fetchReviewsByNationalityAndSector.rejected, (state, action) => {
    state.reviewsByNationalitySectorLoading = false;
    state.errors.push(
      action.payload || 'Erreur lors du chargement des reviews par nationalité'
    );
  });

  },
});

export const { 
  setClaimsFilters, 
  resetClaimsFilters, 
  clearErrors ,
  setUsersEvolutionPeriod 
} = dashboardSlice.actions;

export default dashboardSlice.reducer;



// ============================================================================
// 📄 Utilisation dans le composant principal (index.js)
// ============================================================================

/*
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAllDashboardData, 
  fetchClaimsDashboard, 
  setClaimsFilters 
} from '../../../redux/dashboardSlice';

const DashboardTasks = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  
  const { 
    claims, 
    companies, 
    services, 
    suggestions, 
    finance, 
    users, 
    loading, 
    claimsLoading,
    claimsFilters,
    errors 
  } = useSelector((state) => state.dashboard);

  // Charger les données initiales
  useEffect(() => {
    dispatch(fetchAllDashboardData());
  }, [dispatch]);

  // Handler pour les filtres Claims
  const handleClaimsFilterChange = (filters) => {
    dispatch(setClaimsFilters(filters));
    dispatch(fetchClaimsDashboard(filters));
  };

  return (
    <>
      {activeTab === 'claims' && (
        <ClaimsTab 
          dashboardData={dashboardData}
          safeClaims={safeClaims}
          onFilterChange={handleClaimsFilterChange}
          loading={claimsLoading}
        />
      )}
    </>
  );
};
*/
